import { useState, type ReactNode } from "react";
import { Boxes, Check, Copy, Plug, Webhook } from "lucide-react";
import { Button, Card, CardHeader, Sheet } from "@/ui";
import { cn } from "@/lib/cn";
import { account, type Integration } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { SampleChip } from "./parts";

/** Brand-ish glyph per integration, kept monochrome (no decorative color). */
function IntegrationGlyph({ source }: { source: Integration["source"] }) {
  if (source === "webhook")
    return <Webhook className="h-[18px] w-[18px]" strokeWidth={1.5} />;
  if (source === "mcp") return <Boxes className="h-[18px] w-[18px]" strokeWidth={1.5} />;
  // posthog / matomo — use initial-based mono mark
  return (
    <span className="font-mono text-[13px] font-semibold tracking-tight">
      {source === "posthog" ? "ph" : "mt"}
    </span>
  );
}

const META: Record<string, string> = {
  PostHog: "Events, funnels and session replay sync every 60s.",
  Matomo: "Privacy-first web analytics — no cookie banner needed.",
  "MCP server": "Studio actions exposed as tools for Nara and your own agents.",
  Webhooks: "POST every tracked event to an endpoint you control.",
};

function IntegrationCard({ it, onOpen }: { it: Integration; onOpen: () => void }) {
  const connected = it.status === "connected";
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "grid h-11 w-11 shrink-0 place-items-center rounded-btn",
            connected
              ? "bg-ink-strong text-canvas"
              : "border border-hairline text-muted",
          )}
        >
          <IntegrationGlyph source={it.source} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">{it.name}</h3>
            {connected ? (
              <span className="inline-flex items-center gap-1 font-mono text-[11px] text-emerald-600">
                <Check className="h-3.5 w-3.5" strokeWidth={2} /> connected
              </span>
            ) : (
              <span className="font-mono text-[11px] text-subtle">available</span>
            )}
          </div>
          <p className="mt-0.5 text-[13px] text-muted">{it.desc}</p>
        </div>
      </div>
      <p className="text-[13px] leading-relaxed text-subtle">{META[it.name] ?? it.desc}</p>
      <div className="mt-auto flex items-center justify-between pt-1">
        <span className="font-mono text-[11px] text-subtle">
          {connected ? "synced just now" : "2-minute setup"}
        </span>
        {connected ? (
          <Button variant="quiet" size="sm" onClick={onOpen}>
            Manage
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={onOpen}>
            <Plug className="h-3.5 w-3.5" strokeWidth={1.75} /> Connect
          </Button>
        )}
      </div>
    </Card>
  );
}

/* --------------------------- integration sheets --------------------------- */
/*
 * These are informational sheets, not a real integrations backend. The honest
 * split today:
 *   - PostHog / Matomo  → already built in (they power Signals' first-party
 *                          ingestion); nothing external to connect.
 *   - MCP server        → real (services/mcp-server, stdio); conceptual config.
 *   - Webhooks          → format is final, but persisting an endpoint is gated
 *                          on the integrations-svc (marked "coming soon").
 *
 * DDD TODO — Integrations bounded context (`integrations-svc`).
 * Stand up a dedicated BC that owns *external* connection config: persisted
 * connection records + secrets, OAuth flows where relevant (external sinks),
 * and webhook endpoint registration with HMAC secrets + delivery/retry state.
 * Until it exists, any "save" affordance below stays read-only / coming-soon.
 */

/** Small left-to-right "data flow" chip row used in the analytics sheets. */
function Flow({ steps }: { steps: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {steps.map((s, i) => (
        <span key={s} className="flex items-center gap-1.5">
          <span className="rounded-chip bg-paper-2 px-2 py-1 font-mono text-[11px] text-muted dark:bg-white/5">
            {s}
          </span>
          {i < steps.length - 1 && <span className="text-subtle">→</span>}
        </span>
      ))}
    </div>
  );
}

/** A calm note line; `pending` switches it to the dashed "coming soon" style. */
function SheetNote({ children, pending }: { children: ReactNode; pending?: boolean }) {
  return (
    <p
      className={cn(
        "rounded-btn px-3 py-2 text-[12px] text-subtle",
        pending
          ? "border border-dashed border-hairline"
          : "border border-hairline bg-paper-2 dark:bg-white/5",
      )}
    >
      {children}
    </p>
  );
}

/** PostHog / Matomo — first-party engines that already power Signals. */
function AnalyticsBody({ source }: { source: "posthog" | "matomo" }) {
  const ph = source === "posthog";
  return (
    <div className="space-y-4">
      <div>
        <p className="eyebrow mb-1.5">Analytics engine · built in</p>
        <p className="text-[13px] leading-relaxed text-muted">
          {ph
            ? "PostHog's open-source engine already powers Signals. Events from your install snippet are ingested first-party — funnels, retention and session replay render straight from that store. There is no separate PostHog Cloud account to connect or sync."
            : "Matomo's privacy-first model already powers Signals' web analytics. Visits, pages and sources are computed first-party with no third-party cookies, so you never need a cookie banner. There is no separate Matomo instance to connect."}
        </p>
      </div>
      <div>
        <p className="eyebrow mb-1.5">How data flows</p>
        <Flow
          steps={[
            "install snippet",
            "first-party ingest",
            ph ? "PostHog + rrweb format" : "Matomo visit model",
            "Signals",
          ]}
        />
      </div>
      <SheetNote>
        Nothing to toggle here — this engine is part of Signals, not an external account, so there are
        no keys to paste.
      </SheetNote>
    </div>
  );
}

/** Conceptual MCP client config — the bridge is real (services/mcp-server). */
const mcpClientConfig = `{
  "mcpServers": {
    "landing-platform": {
      "command": "mcp-server",
      "env": {
        "PLATFORM_BASE_URL": "https://api.aigen.solutions",
        "PLATFORM_TOKEN": "<your ZITADEL bearer>"
      }
    }
  }
}`;

const MCP_SAMPLE_TOOLS = [
  "analytics_sites_list",
  "deploys_rollback",
  "experiments_list",
  "forms_list_submissions",
  "ai_chat",
  "assets_upload",
];

/** MCP server — Studio actions exposed as MCP tools over stdio. */
function McpBody() {
  return (
    <div className="space-y-4">
      <div>
        <p className="eyebrow mb-1.5">Agent bridge · live</p>
        <p className="text-[13px] leading-relaxed text-muted">
          <span className="font-mono text-[12px] text-ink-strong">services/mcp-server</span> exposes
          Studio actions — analytics, deploys, forms, experiments, the asset builder and AI chat — as
          Model Context Protocol tools over stdio. Nara drives the platform through it, and you can point
          your own agent (Claude Desktop, Cursor, …) at the same bridge.
        </p>
      </div>
      <div>
        <p className="eyebrow mb-1.5">A few of the tools</p>
        <div className="flex flex-wrap gap-1.5">
          {MCP_SAMPLE_TOOLS.map((t) => (
            <span
              key={t}
              className="rounded-chip bg-paper-2 px-2 py-1 font-mono text-[11px] text-muted dark:bg-white/5"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
      <CodeBlock label="MCP client config" code={mcpClientConfig} />
      <SheetNote>
        Runs locally over stdio against your gateway. The bearer is your ZITADEL token, read from the
        environment — never hard-coded.
      </SheetNote>
    </div>
  );
}

/** Read-only example of a single webhook delivery. */
const webhookExample = `POST https://your-app.example.com/aigen/events
Content-Type: application/json
X-Aigen-Signature: sha256=…   // HMAC of the raw body

{
  "id": "evt_1f9c2a",
  "type": "event.tracked",
  "site_id": "site_SAMPLE0000",
  "occurred_at": "2026-06-25T14:03:11Z",
  "event": {
    "name": "signup_completed",
    "path": "/pricing",
    "anonymous_id": "anon_8d21",
    "props": { "plan": "pro", "ref": "launch" }
  }
}`;

/** Webhooks — honest: format is final, persistence needs the integrations-svc. */
function WebhookBody() {
  return (
    <div className="space-y-4">
      <div>
        <p className="eyebrow mb-1.5">Event stream</p>
        <p className="text-[13px] leading-relaxed text-muted">
          Forward every tracked event to an HTTPS endpoint you control — pipe Signals into your own
          warehouse, a Slack bot, or a workflow engine. Each event is delivered as a single JSON POST,
          signed with an HMAC header so you can verify it came from aigen.
        </p>
      </div>
      <CodeBlock label="Example delivery (read-only)" code={webhookExample} />
      <SheetNote pending>
        <span className="font-mono text-[10px] uppercase tracking-wide">coming soon</span> — saving an
        endpoint URL and signing secret needs the integrations backend. The payload format above is final.
      </SheetNote>
    </div>
  );
}

/** Routes an integration to its setup body by source. */
function IntegrationSheetBody({ it }: { it: Integration }) {
  switch (it.source) {
    case "mcp":
      return <McpBody />;
    case "webhook":
      return <WebhookBody />;
    case "posthog":
    case "matomo":
      return <AnalyticsBody source={it.source} />;
    default:
      return <p className="text-[13px] text-muted">{META[it.name] ?? it.desc}</p>;
  }
}

/** Bottom sheet shell with the integration glyph + name as its title. */
function IntegrationSheet({
  integration,
  onClose,
}: {
  integration: Integration | null;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={!!integration}
      onClose={onClose}
      title={
        integration ? (
          <span className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-btn border border-hairline text-muted">
              <IntegrationGlyph source={integration.source} />
            </span>
            {integration.name}
          </span>
        ) : undefined
      }
    >
      {integration && <IntegrationSheetBody it={integration} />}
    </Sheet>
  );
}

/* ---------------------------- install snippets ---------------------------- */

/** The host an SDK / tag should target (same convention as builder-ui's snippets). */
function ingestHost(): string {
  return typeof window !== "undefined" ? window.location.origin : "https://your-workspace.example";
}

/** Real first-party web tag: the window._dl queue loading the served dl.js. */
function webSnippet(siteId: string): string {
  const h = ingestHost();
  return `<!-- aigen analytics — first-party web tag -->
<script>
  var _dl = window._dl = window._dl || [];
  _dl.push(['setTrackerUrl', '${h}/analytics/v1/collect']);
  _dl.push(['setSiteId', '${siteId}']);
  _dl.push(['setWriteKey', 'YOUR_WRITE_KEY']);
  _dl.push(['trackPageView']);
  (function(){var s=document.createElement('script');
   s.async=1;s.src='${h}/analytics/v1/dl.js';document.head.appendChild(s);})();
</script>`;
}

/** Real iOS: the official PostHog SDK (MIT) pointed at our /ph ingestion. */
function iosSnippet(siteId: string, last4: string): string {
  const h = ingestHost();
  return `// Official PostHog SDK (MIT): github.com/PostHog/posthog-ios
import PostHog

let config = PostHogConfig(
  apiKey: "YOUR_WRITE_KEY",   // write key ending …${last4}
  host: "${h}/analytics/v1/ph/${siteId}")
config.sessionReplay = true
PostHogSDK.shared.setup(config)
// Full step-by-step guide → the SDK tab`;
}

/** Real Android: the official PostHog SDK (MIT) pointed at our /ph ingestion. */
function androidSnippet(siteId: string, last4: string): string {
  const h = ingestHost();
  return `// Official PostHog SDK (MIT): github.com/PostHog/posthog-android
import com.posthog.android.PostHogAndroid
import com.posthog.android.PostHogAndroidConfig

val config = PostHogAndroidConfig(
  apiKey = "YOUR_WRITE_KEY",   // write key ending …${last4}
  host = "${h}/analytics/v1/ph/${siteId}"
).apply { sessionReplay = true }
PostHogAndroid.setup(applicationContext, config)
// Full step-by-step guide → the SDK tab`;
}

function CodeBlock({ label, code }: { label: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="overflow-hidden rounded-btn border border-hairline">
      <div className="flex items-center justify-between border-b border-hairline bg-paper-2 px-3 py-2 dark:bg-white/5">
        <span className="eyebrow">{label}</span>
        <button
          onClick={() => {
            void navigator.clipboard?.writeText(code);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
          }}
          className="inline-flex items-center gap-1 font-mono text-[11px] text-subtle hover:text-ink"
          aria-label="Copy snippet"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-3 font-mono text-[12px] leading-relaxed text-ink">
        <code>{code}</code>
      </pre>
    </div>
  );
}

/** Real install snippet for the current property, keyed to its site_id + write_key. */
function InstallSnippets() {
  const ws = useWorkspace();
  const site = ws.current?.site;
  const isSample = !ws.live || !site;

  // Fall back to a representative sample site when there's no live property.
  const siteId = site?.site_id ?? "site_SAMPLE0000";
  const last4 = site?.write_key_last4 ?? "9f2a";
  const kind = ws.current?.kind ?? "web";

  const blocks =
    kind === "ios"
      ? [{ label: "iOS — Swift", code: iosSnippet(siteId, last4) }]
      : kind === "android"
        ? [{ label: "Android — Kotlin", code: androidSnippet(siteId, last4) }]
        : [{ label: "Web — HTML <head>", code: webSnippet(siteId) }];

  return (
    <Card className="p-5">
      <CardHeader
        title="Install snippet"
        eyebrow={ws.current?.name ?? "Your property"}
        className="px-0 pt-0"
        action={isSample ? <SampleChip /> : undefined}
      />
      <p className="mb-3 text-[13px] text-muted">
        Wired to{" "}
        <span className="font-mono text-[12px] text-ink-strong">{siteId}</span> · write key{" "}
        <span className="font-mono text-[12px] text-ink-strong">····{last4}</span>. Your full write
        key is shown once when the property is created.
      </p>
      <div className="space-y-3">
        {blocks.map((b) => (
          <CodeBlock key={b.label} label={b.label} code={b.code} />
        ))}
      </div>
      {isSample && (
        <p className="mt-3 text-[12px] text-subtle">
          Sample IDs shown — select a live property to generate its real snippet.
        </p>
      )}
    </Card>
  );
}

export function IntegrationsSection() {
  // Connection states have no backend yet — always sample.
  const connected = account.integrations.filter((i) => i.status === "connected").length;
  // Which integration's setup sheet is open (null = closed). Local-only state:
  // see the DDD TODO above — there is no integrations-svc to persist against yet.
  const [active, setActive] = useState<Integration | null>(null);
  return (
    <div className="space-y-4">
      <InstallSnippets />

      <Card className="flex items-center justify-between p-5">
        <div>
          <p className="eyebrow mb-1 flex items-center gap-2">
            Integrations <SampleChip />
          </p>
          <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
            {connected} of {account.integrations.length} connected
          </h3>
        </div>
        <span className="font-mono text-[12px] text-muted">
          1 endpoint · 2 analytics · 1 agent bridge
        </span>
      </Card>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {account.integrations.map((it) => (
          <IntegrationCard key={it.name} it={it} onOpen={() => setActive(it)} />
        ))}
      </div>

      <IntegrationSheet integration={active} onClose={() => setActive(null)} />
    </div>
  );
}
